import AutomaticSvg from "@assets/design/wizard-automatic-workflow.svg?react";
import ManualSvg from "@assets/design/wizard-manual-workflow.svg?react";
import { SphereToggleButtonGroup } from "@components/common/sphere-toggle-button-group";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack, SvgIcon } from "@mui/material";
import { WorkflowType } from "@pages/data-management/data-management-types";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { sphereColors } from "@styles/common-colors";

const SphereToggleButtonGroupPublishType = SphereToggleButtonGroup<WorkflowType>;

interface Props {
  /** Type of the workflow, automatic or manual. */
  workflowType: WorkflowType,
  /** Callback for handling a change of the workflow type toggle button. */
  handleWorkflowTypeChange: (workflowType: WorkflowType) => void,
}

/** Processing and publication options for the selected Blink scans. */
export function SelectModeComponent({
  workflowType,
  handleWorkflowTypeChange,
}: Props): JSX.Element {
  const {
    isExtraSmall,
    isScreenSmOrSmaller,
    isScreenMdAndSmaller,
    isScreenXlOrLarger,
  } = useMediaQueryList();
  const isSmOrMd = isScreenMdAndSmaller && !isExtraSmall;
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);

  let fontSizeTitle;
  if (isExtraSmall) {
    fontSizeTitle = "30px";
  } else if (isScreenSmOrSmaller) {
    fontSizeTitle = "34px";
  } else if (isScreenMdAndSmaller) {
    fontSizeTitle = "42px";
  } else {
    fontSizeTitle = "50px";
  }

  let fontSizeSubtitle;
  if (isExtraSmall) {
    fontSizeSubtitle = "16px";
  } else if (isScreenSmOrSmaller) {
    fontSizeSubtitle = "18px";
  } else if (isScreenMdAndSmaller) {
    fontSizeSubtitle = "20px";
  } else {
    fontSizeSubtitle = "24px";
  }

  return (
    <Stack>
      <Box sx={{
        mb: "30px",
        fontSize: fontSizeTitle,
        fontWeight: "600",
        color: sphereColors.blue400,
      }}>
        Ready to bring your data to Sphere XG?
      </Box>

      <Box sx={{
        mb: "20px",
        fontSize: fontSizeSubtitle,
        fontWeight: "400",
        color: sphereColors.gray600,
      }}>
        Start by selecting how you'd like the import process to run:
      </Box>

      <Box sx={{
        mb: isScreenXlOrLarger ? "60px" : "40px",
        width: "400px",
      }}>
        <SphereTooltip
          title="The manual workflow will be available soon."
          enterDelay={0}
          shouldShowTooltip={!isDevModeEnabled}
        >
          <SphereToggleButtonGroupPublishType
            items={[
              {
                value: "auto",
                label: "Automatic",
              },
              {
                value: "staged",
                label: "Manual review",
              },
            ]}
            currentValue={workflowType}
            handleChange={handleWorkflowTypeChange}
            isDisabled={!isDevModeEnabled}
            sxGroup={{ display: "flex" }}
            sxButtons={{ width: "50%", maxWidth: "50%" }}
          />
        </SphereTooltip>
      </Box>

      <Stack
        direction={ isSmOrMd ? "row-reverse" : "column" }
        sx={{ alignItems: "center" }}
      >
        <Box sx={{
          mb: "30px",
          textAlign: "center",
        }}>
          { workflowType === "auto" ? (
            <SvgIcon
              inheritViewBox
              component={AutomaticSvg}
              sx={{
                width: isScreenXlOrLarger ? "258px" : "215px",
                height: isScreenXlOrLarger ? "255px" : "213px",
              }}
            />
          ) : (
            <SvgIcon
              inheritViewBox
              component={ManualSvg}
              sx={{
                width: isScreenXlOrLarger ? "258px" : "215px",
                height: isScreenXlOrLarger ? "250px" : "209px",
                mb: isScreenXlOrLarger ? "5px" : "4px",
              }}
            />
          )}
        </Box>

        <Box sx={{
          width: isSmOrMd ? undefined : "80%",
          ml: isSmOrMd ? undefined : "auto",
          mr: isSmOrMd ? "40px" : "auto",
          mb: isSmOrMd ? undefined : "40px",
          textAlign: "center",
          fontSize: "16px",
          fontWeight: "400",
          color: sphereColors.gray600,
        }}>
          { workflowType === "auto" ? (
            <>
              Automatically processes, registers, and publishes scans upon import, making them immediately available.
            </>
          ) : (
            <>
              Processes and registers the scans without publishing, allowing for further review and adjustments
              before making them available.
            </>
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
